import { Box, useTheme } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { BarSeriesType } from '@mui/x-charts/models';
import { MakeOptional } from '@mui/x-charts/models/helpers';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';

import { ReportChartProps } from '../types';

import { groupDataByMonth } from './groupDataByMonth';
import { generateLabels, parseAssignedData } from './parseAssignedData';

export const ReportChart = ({
  assignedDataLabel = 'Assigned',
  endDate,
  schedules,
  hideInfo = false,
  hideAverage = false,
  report,
  startDate,
  xAxisLabel,
  completedDate,
  yearAggregation = 'average',
}: ReportChartProps) => {
  const graphStart = dayjs(startDate);
  const graphEnd = dayjs(endDate);

  const theme = useTheme();
  const isYear = useMemo(() => {
    return graphEnd.diff(graphStart, 'month') === 11;
  }, [startDate, endDate]);
  const showAssigned = schedules.length > 0;
  const labels = useMemo(() => generateLabels(startDate, endDate), [startDate, endDate]);

  const stopDate = completedDate
    ? dayjs(completedDate).subtract(1, 'day').format('YYYY-MM-DD')
    : endDate;

  const {
    average,
    averageString: averageMetric,
    data,
    metric: dataLabel,
    total,
    totalString: totalMetric,
  } = report;

  const setData = labels.map((label) => {
    const dataPoint = data.find(
      (d) =>
        dayjs(d.date).format('YYYY-MM-DD') === label ||
        dayjs(d.date).format('YYYY-MM') === label,
    );
    return dataPoint ? dataPoint.completed : 0;
  });

  const setActualSubmissionDate: string[] = labels.map((label) => {
    const dataPoint = data.find((d) => dayjs(d.date).format('YYYY-MM-DD') === label);
    return dataPoint ? dataPoint.actualSubmissionDate : '';
  });

  // @ts-ignore
  const seriesData: (MakeOptional<BarSeriesType, 'type'> | undefined)[] = [
    showAssigned && {
      data: parseAssignedData(schedules, startDate, endDate, stopDate, labels),
      label: assignedDataLabel,
      id: 'assignedId',
      // @ts-ignore
      color: theme.palette.grey.main,
      valueFormatter: (v: number | null) => {
        return v != null && v > 0 ? 'YES' : 'NO';
      },
    },
    {
      data: isYear ? groupDataByMonth(data, labels, yearAggregation) : setData,
      label: dataLabel,
      id: 'setId',
      color: theme.palette.primary.main,
      valueFormatter: (v: number | null, index: { dataIndex: number }) => {
        return (
          `${v} ` +
          `${total && !hideInfo ? `| ${totalMetric}: ${total || 'Total'}` : ''} ` +
          `${average && !hideInfo && !hideAverage ? ` |  ${averageMetric || 'averageMetric'}: ${average}` : ''}` +
          `${setActualSubmissionDate[index.dataIndex] && !isYear ? `| Date Completed: ${setActualSubmissionDate[index.dataIndex]}` : ''}`
        );
      },
    },
  ].filter(Boolean); // Filter out undefined series if `assignedData` is not provided

  return (
    <Box sx={{ height: 500 }}>
      <BarChart
        borderRadius={5}
        // @ts-ignore
        series={seriesData}
        xAxis={[
          {
            // @ts-ignore
            barGapRatio: -1,
            categoryGapRatio: 0.2,
            data: labels,
            label: xAxisLabel,
            scaleType: 'band',
          },
        ]}
        yAxis={[{ label: dataLabel }]}
      />
    </Box>
  );
};
